import React, { useState, useCallback, useRef } from 'react';
import { connect } from 'redux-zero/react';
import { useTranslation } from 'react-i18next';
import { formActions } from 'app/state/actions';
import TableWrapper from 'app/components/TableWrapper/TableWrapper';
import OrdersWrapper from '../OrdersWrapper';
import RedemptionStatusType from './RedemptionStatusType';
import { currencyFormat, datetimeFormat, dsConfirm } from '@tokenyico/design-system';
import { DsIcon, DsTooltip, DsButton, DsDialog } from '@tokenyico/design-system/dist/reactJS';
import { RedemptionService } from 'services';
import TokenAmountCell from 'app/components/Cells/TokenAmountCell';
import RedemptionOrderDetailsView from './RedemptionOrderDetailsView';
import RedemptionPaymentInfoView from './RedemptionPaymentInfoView';
import OrderDetailsTabView from 'app/components/Orders/OrderDetailsTabView';

import './RedemptionOrder.less';

const EmptyState = () => {
  const { t } = useTranslation();
  return (
    <div className="empty-state">
      <div className="empty-state__title">{t('emptyState.noRedemptionOrders.title')}</div>
      <div className="empty-state__body">{t('emptyState.noRedemptionOrders.body')}</div>
    </div>
  );
};

const RedemptionOrdersView = ({ setError, setSuccess, holder: { selectedToken, redemption } }) => {
  const { t } = useTranslation();

  const detailsDialogRef = useRef();

  const [tableData, setTableData] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState();
  const [isOrderOpen, setOrderOpen] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState({});
  const [actionLoadingRow, setActionLoadingRow] = useState({});
  const [totalFoundRows, setTotalFoundRows] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [lastFetch, setLastFetch] = useState({});

  const { redemptionId } = redemption;

  const header = [
    {
      key: 'viewDetails',
      label: '',
    },
    {
      key: 'actions',
      label: t('shared.actions'),
    },
    {
      key: 'createdAt',
      label: t('shared.creationDate'),
    },
    {
      key: 'status',
      label: t('shared.status'),
    },
    {
      key: 'tokenAmount',
      label: t('shared.tokensToRedeem'),
    },
    {
      key: 'tokenPrice',
      label: t('shared.tokenPrice'),
    },
    {
      key: 'tokenPriceAt',
      label: t('shared.tokenPriceTime'),
    },
    {
      key: 'totalRedemptionAmount',
      label: t('shared.amountToReceive'),
    },
  ];

  const loadData = useCallback(
    async (detail) => {
      const queryParams = {
        limit: detail.rowsPerPage,
        offset: (detail.currentPage - 1) * detail.rowsPerPage,
      };
      setLastFetch(detail);
      const ordersRes = await RedemptionService.getRedemptionOrders({
        redemptionId,
        ...queryParams,
      });
      const exchangeRates = await RedemptionService.getExchangeRates(redemptionId);
      const orders = ordersRes.items.map((order) => ({
        ...order,
        baseCurrency: exchangeRates[0].baseCurrency,
      }));
      setTableData(orders);
      setTotalRows(ordersRes.totalItemsFound);
      if (totalFoundRows === 0) setTotalFoundRows(ordersRes.totalItemsFound);
    },
    [setError],
  );

  const getRedemptionOrder = useCallback(
    async (order) => {
      try {
        if (redemptionId) {
          const {
            paymentInformation: { iban, bicSwift, accountOwner, bankName },
          } = await RedemptionService.getRedemptionOrder({
            redemptionId,
            orderId: order.id,
          });
          setPaymentInfo({
            iban,
            bicSwift,
            accountOwner,
            bankName,
          });
        }
      } catch (e) {
        setError(e);
      }
    },
    [redemption, setError],
  );

  const handleCancelOrder = async (order) => {
    setActionLoadingRow(order);
    let action = '';
    let content = '';
    try {
      switch (order.status) {
        case 'INITIATED':
        case 'PENDING_TO_ACCEPT':
          action = t('shared.cancelOrder');
          content = t('shared.cancelOrderContent');
          break;
        case 'PENDING':
          action = t('shared.createCancelRequest');
          content = t('shared.createCancelRequestContent');
          break;
        default:
          break;
      }

      if (
        await dsConfirm({
          heading: `${action} ?`,
          content,
          action: `${t('shared.yes')}, ${action.toLocaleLowerCase()}`,
          version: 'danger',
        })
      ) {
        await RedemptionService.cancelRedemptionOrder({
          redemptionId,
          orderId: order.id,
        });

        setSuccess(t('shared.redemptionOrdersDialogSuccess'));
        //reload
        await loadData(lastFetch);
      }
      setActionLoadingRow({});
    } catch (e) {
      setError(e);
      setActionLoadingRow({});
    }
  };

  const handleShowOrder = async (row) => {
    setSelectedOrder(row);
    getRedemptionOrder(row);
    setOrderOpen(true);
    detailsDialogRef.current.open();
  };

  return (
    <OrdersWrapper currentTab={1} hasRedemption={true}>
      <TableWrapper
        width="100%"
        header={header}
        data={tableData}
        totalRows={totalRows}
        rowsPerPage={10}
        onFetchData={loadData}
        noData={() => <EmptyState />}
        // rendering functions
        viewDetails={({ row }) => (
          <DsButton
            mode="text"
            icon="view"
            iconSize="1rem"
            onClick={() => handleShowOrder(row)}
            class="_p(0)"
          />
        )}
        actions={({ row }) =>
          ['PENDING', 'INITIATED', 'PENDING_TO_ACCEPT'].includes(row.status) && !row?.tokenPrice ? (
            <DsButton
              label={t('shared.cancel')}
              icon="cross-circle"
              iconSize="16px"
              isMini
              isReverse
              version="error"
              onClick={() => handleCancelOrder(row)}
              isLoading={row.id === actionLoadingRow.id}
            />
          ) : null
        }
        createdAt={({ value }) => datetimeFormat(value)}
        status={({ value }) => <RedemptionStatusType status={value} />}
        tokenAmount={({ value }) => <TokenAmountCell amount={value} />}
        tokenPrice={({ value, row }) =>
          ['CANCELED_BY_INVESTOR', 'CANCELED_BY_ISSUER'].includes(row.status) ? (
            '-'
          ) : value ? (
            currencyFormat(value, selectedToken.currency, true)
          ) : (
            <DsTooltip
              theme="light"
              isVisibleOnHover={true}
              text={t('shared.tokenPriceSetByIssuer')}
            >
              <DsIcon name="help" iconSize="14px" color="var(--warning)" />
            </DsTooltip>
          )
        }
        tokenPriceAt={({ value, row }) =>
          ['CANCELED_BY_INVESTOR', 'CANCELED_BY_ISSUER'].includes(row.status) ? (
            '-'
          ) : value ? (
            datetimeFormat(value)
          ) : (
            <DsIcon name="pending" iconSize="14px" color="var(--warning)" />
          )
        }
        totalRedemptionAmount={({ value, row }) =>
          ['CANCELED_BY_INVESTOR', 'CANCELED_BY_ISSUER'].includes(row.status) ? (
            '-'
          ) : value ? (
            <span className="total-payment__badge">
              {currencyFormat(value, row.paymentCurrency, true)}
            </span>
          ) : (
            <DsTooltip theme="light" isVisibleOnHover={true} text={t('shared.amountToPayNull')}>
              <DsIcon name="help" iconSize="14px" color="var(--warning)" />
            </DsTooltip>
          )
        }
      />
      <DsDialog
        ref={detailsDialogRef}
        isModal={true}
        isDismissible={true}
        onClosed={() => setOrderOpen(false)}
        id="redemptionOrderDialog"
      >
        <div>
          {isOrderOpen ? (
            <OrderDetailsTabView
              onCloseModal={() => detailsDialogRef.current.close()}
              actions={
                ['PENDING', 'INITIATED', 'PENDING_TO_ACCEPT'].includes(selectedOrder.status) &&
                !selectedOrder?.tokenPrice ? (
                  <DsButton
                    label={t('shared.cancelOrder')}
                    version="error"
                    onClick={() => {
                      handleCancelOrder(selectedOrder);
                      detailsDialogRef.current.close();
                    }}
                  />
                ) : null
              }
            >
              {/* add title props each child to define tab title */}
              <RedemptionOrderDetailsView title={t('shared.orderDetails')} order={selectedOrder} />
              <RedemptionPaymentInfoView
                title={t('shared.paymentInfo')}
                order={selectedOrder}
                paymentInfo={paymentInfo}
              />
            </OrderDetailsTabView>
          ) : null}
        </div>
      </DsDialog>
    </OrdersWrapper>
  );
};

const mapToProps = (state) => state;

export default connect(mapToProps, formActions)(RedemptionOrdersView);
